/* ==========================================================================
   AJAZZ Hero Slider — vanilla, dependency-free custom element.
   One instance per <ajazz-hero-slider>; auto (re)inits on Theme Editor
   section load via connectedCallback / disconnectedCallback.
   ========================================================================== */
(function () {
  'use strict';

  if (window.customElements && customElements.get('ajazz-hero-slider')) return;

  var REDUCED = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

  var AjazzHeroSlider = class extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.timer = null;
      this.isPaused = false;
      // Bound handlers so add/removeEventListener match.
      this._onPrev = this._onPrev.bind(this);
      this._onNext = this._onNext.bind(this);
      this._onDot = this._onDot.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onEnter = this.pause.bind(this);
      this._onLeave = this.resume.bind(this);
      this._onFocusIn = this.pause.bind(this);
      this._onFocusOut = this._onFocusOut.bind(this);
      this._onVisibility = this._onVisibility.bind(this);
      this._onTouchStart = this._onTouchStart.bind(this);
      this._onTouchEnd = this._onTouchEnd.bind(this);
    }

    connectedCallback() {
      // Guard against double init.
      if (this._ready) return;

      this.slides = Array.prototype.slice.call(
        this.querySelectorAll('[data-ajazz-slide]')
      );
      if (this.slides.length === 0) return;

      this.viewport = this.querySelector('[data-ajazz-viewport]');
      this.dots = Array.prototype.slice.call(
        this.querySelectorAll('[data-ajazz-dot]')
      );
      this.prevBtn = this.querySelector('[data-ajazz-prev]');
      this.nextBtn = this.querySelector('[data-ajazz-next]');

      this.anim = this.dataset.anim === 'fade' ? 'fade' : 'slide';
      this.autoplayEnabled =
        this.dataset.autoplay === 'true' && !REDUCED.matches;
      this.speed = parseInt(this.dataset.speed, 10);
      if (isNaN(this.speed) || this.speed < 1000) this.speed = 5000;
      this.duration = parseInt(this.dataset.duration, 10);
      if (isNaN(this.duration)) this.duration = 600;
      this.single = this.slides.length <= 1;

      // Normalize active state to the first slide.
      this.index = Math.max(
        0,
        this.slides.findIndex(function (s) {
          return s.classList.contains('is-active');
        })
      );
      if (this.index < 0) this.index = 0;
      this._applyState(this.index, this.index, 1, true);

      this._bind();
      if (this.autoplayEnabled && !this.single) this._start();

      // Decode the other slide images while the browser is idle so navigating
      // to them is instant instead of janking on first paint.
      if (!this.single) {
        var idle =
          window.requestIdleCallback ||
          function (cb) {
            return window.setTimeout(cb, 1500);
          };
        idle(this._primeImages.bind(this));
      }

      this._ready = true;
    }

    _primeImages() {
      var imgs = this.querySelectorAll('.ajazz-hero__img');
      Array.prototype.forEach.call(imgs, function (img) {
        if (img.getAttribute('loading') === 'lazy') {
          img.setAttribute('loading', 'eager');
        }
        if (typeof img.decode === 'function') {
          img.decode().catch(function () {});
        }
      });
    }

    disconnectedCallback() {
      this._stop();
      this._unbind();
      this._ready = false;
    }

    /* ---- Event wiring ---------------------------------------------------- */
    _bind() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);
      this.dots.forEach(
        function (dot) {
          dot.addEventListener('click', this._onDot);
        }.bind(this)
      );

      if (!this.single) {
        this.addEventListener('keydown', this._onKeydown);
      }

      if (this.autoplayEnabled && !this.single) {
        this.addEventListener('mouseenter', this._onEnter);
        this.addEventListener('mouseleave', this._onLeave);
        this.addEventListener('focusin', this._onFocusIn);
        this.addEventListener('focusout', this._onFocusOut);
        document.addEventListener('visibilitychange', this._onVisibility);
      }

      if (this.viewport && !this.single) {
        this.viewport.addEventListener('touchstart', this._onTouchStart, {
          passive: true
        });
        this.viewport.addEventListener('touchend', this._onTouchEnd, {
          passive: true
        });
      }
    }

    _unbind() {
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNext);
      (this.dots || []).forEach(
        function (dot) {
          dot.removeEventListener('click', this._onDot);
        }.bind(this)
      );
      this.removeEventListener('keydown', this._onKeydown);
      this.removeEventListener('mouseenter', this._onEnter);
      this.removeEventListener('mouseleave', this._onLeave);
      this.removeEventListener('focusin', this._onFocusIn);
      this.removeEventListener('focusout', this._onFocusOut);
      document.removeEventListener('visibilitychange', this._onVisibility);
      if (this.viewport) {
        this.viewport.removeEventListener('touchstart', this._onTouchStart);
        this.viewport.removeEventListener('touchend', this._onTouchEnd);
      }
    }

    /* ---- Handlers -------------------------------------------------------- */
    _onPrev() {
      this.go(this.index - 1, -1, true);
    }
    _onNext() {
      this.go(this.index + 1, 1, true);
    }
    _onDot(e) {
      var target = parseInt(e.currentTarget.getAttribute('data-ajazz-dot'), 10);
      if (isNaN(target) || target === this.index) return;
      this.go(target, target > this.index ? 1 : -1, true);
    }
    _onKeydown(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this._onPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this._onNext();
      }
    }
    _onFocusOut(e) {
      // Only resume when focus truly leaves the slider.
      if (!this.contains(e.relatedTarget)) this.resume();
    }
    _onVisibility() {
      if (document.hidden) this._stop();
      else if (!this.isPaused && this.autoplayEnabled && !this.single)
        this._start();
    }
    _onTouchStart(e) {
      this._touchX = e.changedTouches[0].clientX;
      this.pause();
    }
    _onTouchEnd(e) {
      if (typeof this._touchX !== 'number') return;
      var dx = e.changedTouches[0].clientX - this._touchX;
      this._touchX = null;
      if (Math.abs(dx) > 40) {
        if (dx < 0) this._onNext();
        else this._onPrev();
      }
      this.resume();
    }

    /* ---- Core ------------------------------------------------------------ */
    go(target, dir, userTriggered) {
      var count = this.slides.length;
      if (count <= 1) return;
      // Infinite wrap.
      var next = ((target % count) + count) % count;
      if (next === this.index) return;
      var from = this.index;
      this.index = next;
      this._applyState(next, from, dir || 1, false);

      // Restart autoplay clock after a manual interaction.
      if (userTriggered && this.autoplayEnabled && !this.isPaused && !this.single) {
        this._start();
      }
    }

    _applyState(next, from, dir, initial) {
      var fade = this.anim === 'fade' || REDUCED.matches;
      var dur = REDUCED.matches ? 0 : this.duration;
      var ease = 'cubic-bezier(0.4, 0, 0.2, 1)';

      // Classes / ARIA / dots (cheap, always).
      this.slides.forEach(function (el, i) {
        var active = i === next;
        el.classList.toggle('is-active', active);
        if (active) el.removeAttribute('aria-hidden');
        else el.setAttribute('aria-hidden', 'true');
      });
      this.dots.forEach(function (dot, i) {
        var active = i === next;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      // Initial paint: show target instantly, hide the rest. No animation.
      if (initial) {
        this.slides.forEach(function (el, i) {
          var active = i === next;
          el.style.transition = 'none';
          el.style.transform = 'translate3d(0,0,0)';
          el.style.opacity = active ? '1' : '0';
          el.style.visibility = active ? 'visible' : 'hidden';
          el.style.zIndex = active ? '2' : '0';
        });
        return;
      }

      var cur = this.slides[from];
      var nxt = this.slides[next];

      if (fade) {
        // Fade only — no horizontal movement.
        nxt.style.transition = 'none';
        nxt.style.transform = 'translate3d(0,0,0)';
        nxt.style.visibility = 'visible';
        nxt.style.zIndex = '2';
        nxt.style.opacity = '0';
        void nxt.offsetWidth; // reflow
        nxt.style.transition = 'opacity ' + dur + 'ms ' + ease;
        nxt.style.opacity = '1';

        cur.style.zIndex = '1';
        cur.style.transition = 'opacity ' + dur + 'ms ' + ease;
        cur.style.opacity = '0';
      } else {
        // Slide — both stay fully opaque, only transform animates (GPU-smooth).
        nxt.style.transition = 'none';
        nxt.style.opacity = '1';
        nxt.style.visibility = 'visible';
        nxt.style.zIndex = '2';
        nxt.style.transform = 'translate3d(' + dir * 100 + '%,0,0)';
        void nxt.offsetWidth; // reflow so the return to 0 animates
        nxt.style.transition = 'transform ' + dur + 'ms ' + ease;
        nxt.style.transform = 'translate3d(0,0,0)';

        cur.style.opacity = '1';
        cur.style.visibility = 'visible';
        cur.style.zIndex = '1';
        cur.style.transition = 'transform ' + dur + 'ms ' + ease;
        cur.style.transform = 'translate3d(' + dir * -100 + '%,0,0)';
      }

      // After the animation, park every non-current slide back off-stage.
      var token = (this._tid = (this._tid || 0) + 1);
      var self = this;
      window.setTimeout(function () {
        if (token !== self._tid) return;
        self.slides.forEach(function (el, i) {
          if (i === self.index) return;
          el.style.transition = 'none';
          el.style.transform = 'translate3d(0,0,0)';
          el.style.opacity = '0';
          el.style.visibility = 'hidden';
          el.style.zIndex = '0';
        });
      }, dur + 60);
    }

    /* ---- Autoplay -------------------------------------------------------- */
    _start() {
      this._stop();
      if (!this.autoplayEnabled || this.single) return;
      this.timer = window.setInterval(
        function () {
          this.go(this.index + 1, 1, false);
        }.bind(this),
        this.speed
      );
    }
    _stop() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }
    pause() {
      this.isPaused = true;
      this._stop();
    }
    resume() {
      this.isPaused = false;
      if (this.autoplayEnabled && !this.single && !document.hidden) {
        this._start();
      }
    }
  };

  customElements.define('ajazz-hero-slider', AjazzHeroSlider);
})();
